import { styled, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React, { useState } from 'react';

import { modifyTone } from '../../../services/dealGPT';
import { ProjectRunAnalysisQuestion } from '../../../Types/dealGPT';
import { IntellioAdvTone, IntellioAdvToneDisplayMap } from '../../../Types/enums';
import { AnalyticsEvent, useAnalytics } from '../../Providers/AnalyticsProvider';

const ModifyToneContainer = styled('div')({
  display: 'flex',
  gap: '16px',
  alignItems: 'center',
  flexWrap: 'wrap',
});

type ToneChipProps = {
  selected: boolean;
};

const ToneChip = styled(Typography)<ToneChipProps>(({ selected }) => ({
  backgroundColor: selected ? '#CED7E6' : '#E8EEF8',
  color: selected ? '#ffffff' : '#092B49',
  padding: '8px 16px',
  borderRadius: '100px',
  fontSize: '14px',
  cursor: 'pointer',
}));

interface ModifyToneProps {
  projectId: string;
  question?: ProjectRunAnalysisQuestion | null;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  refetchQuestions: () => void;
}

const ModifyTone: React.FC<ModifyToneProps> = ({
  projectId,
  question,
  setIsLoading,
  refetchQuestions,
}) => {
  const [selectedTone, setSelectedtone] = useState<IntellioAdvTone>();
  const analytics = useAnalytics();

  const handleToneClick = async (tone: IntellioAdvTone) => {
    // send prompt with input
    setSelectedtone(tone);
    setIsLoading(true);
    if (question) {
      try {
        analytics.event(
          AnalyticsEvent.WM_IA_A_MODIFY_TONE,
          {
            id: question.id,
            value: question.pageName,
          },
          {
            projectId,
            questionId: question.id,
            newTone: tone,
            pageName: question.pageName,
            runId: question.runId,
            scope: question.scope,
          }
        );
        await modifyTone(tone, question);
        refetchQuestions();
        setSelectedtone(undefined);
      } catch (error) {
        enqueueSnackbar('Failed to modify tone. Please try again later.', {
          variant: 'error',
        });
        return;
      }
    }
  };

  return (
    <ModifyToneContainer>
      <Typography sx={{ fontSize: '14px', color: '#757575' }}>Modify Voice/Tone</Typography>
      <ModifyToneContainer>
        {(Object.keys(IntellioAdvTone) as Array<keyof typeof IntellioAdvTone>).map((tone) => (
          <ToneChip
            key={tone}
            selected={selectedTone === IntellioAdvTone[tone]}
            onClick={() => handleToneClick(IntellioAdvTone[tone])}
          >
            {IntellioAdvToneDisplayMap[IntellioAdvTone[tone]]}
          </ToneChip>
        ))}
      </ModifyToneContainer>
    </ModifyToneContainer>
  );
};

export default ModifyTone;
